import { useFormik } from "formik";
import Select from "../../../components/select";
import * as Yup from "yup";
import Input from "../../../components/input";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import TextArea from "../../../components/textArea";
import { createNetworkSubnet } from "../../../hooks/local/userReducer";


// Helper function to calculate suggested IP ranges from CIDR
const calculateSuggestedRanges = (cidr) => {
  if (!cidr) return null;

  try {
    const [network, prefixLength] = cidr.split('/');
    if (!network || !prefixLength) return null;

    const networkParts = network.split('.').map(Number);
    const prefix = parseInt(prefixLength);

    if (networkParts.length !== 4 || networkParts.some(part => isNaN(part) || part < 0 || part > 255)) {
      return null;
    }

    // Calculate network range
    const mask = (0xffffffff << (32 - prefix)) >>> 0;
    const networkInt = (networkParts[0] << 24) + (networkParts[1] << 16) + (networkParts[2] << 8) + networkParts[3];
    const networkStart = (networkInt & mask);
    const networkEnd = (networkStart | (0xffffffff >>> prefix));

    // Helper to convert integer to IP
    const intToIp = (int) => {
      return [
        (int >>> 24) & 255,
        (int >>> 16) & 255,
        (int >>> 8) & 255,
        int & 255
      ].join('.');
    };

    // Calculate usable range (excluding network and broadcast addresses for /24 and smaller)
    let usableStart = networkStart;
    let usableEnd = networkEnd;

    if (prefix >= 24) {
      usableStart = networkStart + 1; // Skip network address
      usableEnd = networkEnd - 1;     // Skip broadcast address
    }

    // Suggest ranges, avoiding common gateway IPs (.1, .254)
    const suggestions = [];

    // First range: start from .10 to avoid common gateway/infrastructure IPs
    const range1Start = Math.max(usableStart, networkStart + 10);
    const range1End = Math.min(range1Start + 100, usableEnd);

    if (range1Start <= range1End && range1Start >= networkStart && range1End <= networkEnd) {
      suggestions.push({
        start: intToIp(range1Start),
        end: intToIp(range1End),
        description: "General allocation range (avoids infrastructure IPs)"
      });
    }

    // Second range: higher range if there's space
    const range2Start = range1End + 10;
    const range2End = Math.min(range2Start + 50, usableEnd);

    if (range2Start <= range2End && range2Start >= networkStart && range2End <= networkEnd) {
      suggestions.push({
        start: intToIp(range2Start),
        end: intToIp(range2End),
        description: "Additional allocation range"
      });
    }

    return {
      networkStart: intToIp(networkStart),
      networkEnd: intToIp(networkEnd),
      usableStart: intToIp(usableStart),
      usableEnd: intToIp(usableEnd),
      suggestions
    };
  } catch (error) {
    return null;
  }
};
const validateAllocationPools = (pools, cidr) => {
  if (!pools.length || !cidr) return true;

  try {
    // Parse CIDR (e.g., "192.168.0.0/24")
    const [network, prefixLength] = cidr.split('/');
    if (!network || !prefixLength) return false;

    const networkParts = network.split('.').map(Number);
    const prefix = parseInt(prefixLength);

    // Validate network parts
    if (networkParts.length !== 4 || networkParts.some(part => isNaN(part) || part < 0 || part > 255)) {
      return false;
    }

    // Calculate network range
    const mask = (0xffffffff << (32 - prefix)) >>> 0;
    const networkInt = (networkParts[0] << 24) + (networkParts[1] << 16) + (networkParts[2] << 8) + networkParts[3];
    const networkStart = (networkInt & mask);
    const networkEnd = (networkStart | (0xffffffff >>> prefix));


    // Helper to convert IP to integer
    const ipToInt = (ip) => {
      const parts = ip.split('.').map(Number);
      if (parts.length !== 4 || parts.some(part => isNaN(part) || part < 0 || part > 255)) {
        throw new Error('Invalid IP format');
      }
      return (parts[0] << 24) + (parts[1] << 16) + (parts[2] << 8) + parts[3];
    };

    // Helper to convert integer to IP for debugging
    const intToIp = (int) => {
      return [
        (int >>> 24) & 255,
        (int >>> 16) & 255,
        (int >>> 8) & 255,
        int & 255
      ].join('.');
    };

    // Validate each pool
    for (const pool of pools) {
      if (!pool.includes(',')) {
        return false; // Must contain comma
      }

      const parts = pool.split(',');
      if (parts.length !== 2) {
        return false; // Must have exactly 2 parts
      }

      const [start, end] = parts.map(ip => ip.trim());
      if (!start || !end) {
        return false; // Both start and end must be provided
      }

      try {
        const startInt = ipToInt(start);
        const endInt = ipToInt(end);

        // Check if start is less than or equal to end
        if (startInt > endInt) {
          return false;
        }

        // Check if both start and end are within network range
        if (startInt < networkStart || startInt > networkEnd ||
          endInt < networkStart || endInt > networkEnd) {
          return false;
        }
      } catch (error) {
        return false; // Invalid IP format
      }
    }

    return true;
  } catch (error) {
    return false; // Invalid CIDR or IP format
  }
};

const CreateSubnet = () => {
  document.title = "Create subnet - Cloudvantage";

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectedProjected = useSelector((state) => state.user.selectedProject);
  const selectedNetwork = JSON.parse(sessionStorage.getItem("selectedNetwork"));

  const [selectedTab, setSelectedTab] = useState("Subnet");
  const [suggestedRanges, setSuggestedRanges] = useState(null);


  const ipVersionOptions = [
    { value: "IPv4", label: "IPv4" },
    { value: "IPv6", label: "IPv6" },
  ];

  const createSubnet = useFormik({
    initialValues: {
      subnetName: "",
      networkAddress: "",
      ipVersion: "IPv4",
      gatewayIp: "",
      disableGateway: false,
      enableDhcp: true,
      allocationPoolsText: "", // Store as string for textarea
      dnsServersText: "", // Store as string for textarea
      subnetpoolId: "",
    },

    validationSchema: Yup.object({
      subnetName: Yup.string().required("Subnet name cannot be empty"),
      networkAddress: Yup.string().required("Network address cannot be empty"),
      allocationPoolsText: Yup.string().test(
        'pools-validation',
        function (value) {
          if (!value || !this.parent.networkAddress) return true;

          const cidr = this.parent.networkAddress;
          const pools = value.split('\n').map(line => line.trim()).filter(line => line !== '');

          // Check format first
          for (const pool of pools) {
            if (!pool.includes(',')) {
              return this.createError({
                message: `Invalid format: "${pool}". Use format: start_ip,end_ip`
              });
            }

            const parts = pool.split(',');
            if (parts.length !== 2) {
              return this.createError({
                message: `Invalid format: "${pool}". Each line must have exactly one comma separating start and end IP`
              });
            }

            const [start, end] = parts.map(ip => ip.trim());
            if (!start || !end) {
              return this.createError({
                message: `Invalid format: "${pool}". Both start and end IP addresses are required`
              });
            }
          }

          // Check if pools are within CIDR range
          if (!validateAllocationPools(pools, cidr)) {
            return this.createError({
              message: `Allocation pools must be within the CIDR range ${cidr}. Ensure all IP addresses are valid and within the network range.`
            });
          }

          return true;
        }
      ),
    }),

    onSubmit: async (values, { resetForm }) => {
      const { subnetName, networkAddress, ipVersion, gatewayIp, disableGateway, enableDhcp, allocationPoolsText, dnsServersText } = values;

      const transformedIP = ipVersion.replace("IPv", "");

      // Convert allocation pools text to array of objects
      const allocationPools = allocationPoolsText
        ? allocationPoolsText
          .split("\n")
          .map(line => line.trim())
          .filter(line => line !== "")
          .map(pool => {
            const [start, end] = pool.split(",").map(ip => ip.trim());
            return { start, end };
          })
        : [];

      // Convert DNS servers text to array
      const dnsServers = dnsServersText
        ? dnsServersText
          .split("\n")
          .map(line => line.trim())
          .filter(line => line !== "")
        : [];

      let subnetData = {
        name: subnetName,
        cidr: networkAddress,
        ip_version: parseInt(transformedIP),
        projectId: selectedProjected.id,
        networkId: selectedNetwork?.networkId,
        enable_dhcp: enableDhcp,
        allocation_pools: allocationPools.length > 0 ? allocationPools : undefined,
        dns_nameservers: dnsServers.length > 0 ? dnsServers : undefined,
      };

      const { payload } = await dispatch(createNetworkSubnet(subnetData));
      if (payload.statuscode === "00") {
        navigate("/networks");
        resetForm();
      }
    },
  });

  const subnetTabs = [
    "Subnet",
    "Subnet Details",
  ];

  // Calculate suggested ranges when network address changes
  useEffect(() => {
    if (createSubnet.values.networkAddress) {
      const suggestions = calculateSuggestedRanges(createSubnet.values.networkAddress);
      setSuggestedRanges(suggestions);
    } else {
      setSuggestedRanges(null);
    }
  }, [createSubnet.values.networkAddress]);

  const [isSubnetTabValid, setIsSubnetTabValid] = useState(false);

  useEffect(() => {
    const validateFields = async () => {
      try {
        await createSubnet.validateForm();
        const errors = createSubnet.errors;
        const values = createSubnet.values;

        // Check if fields are filled and valid
        const isValid =
          values.subnetName.trim() !== "" &&
          values.networkAddress.trim() !== "" &&
          !errors.subnetName &&
          !errors.networkAddress;
        setIsSubnetTabValid(isValid);
      } catch (error) {
        setIsSubnetTabValid(false);
      }
    };
    validateFields();
  }, [createSubnet.values.subnetName, createSubnet.values.networkAddress, createSubnet.errors]);

  const handleNext = async () => {
    if (selectedTab === "Subnet") {
      // Trigger validation for Subnet tab fields
      const errors = await createSubnet.validateForm();
      if (!errors.subnetName && !errors.networkAddress) {
        setSelectedTab("Subnet Details");
      } else {
        // Touch fields to display errors
        createSubnet.setTouched({
          subnetName: true,
          networkAddress: true,
        });
      }
    }
  };

  const handlePrevious = () => {
    if (selectedTab === "Subnet Details") {
      setSelectedTab("Subnet");
    }
  };

  return (
    <div className="min-h-screen px-4 md:px-10 pt-6 pb-10 dark:text-white">
      <p className="crimson-font text-lg font-medium text-primaryBlue">Create Subnet</p>

      <p className="dark:text-gray-500 text-gray-400 text-sm mb-6 leading-loose md:leading-normal">
        Create a subnet associated with the network. Advanced configuration is available by clicking on the "Subnet Details" tab.
      </p>

      <div className="flex flex-col justify-start items-start w-full border-b dark:border-b-gray-500 border-b-gray-400">
        <div className="flex justify-start items-center w-full md:w-[50%]">
          {subnetTabs.map((tab, i) => (
            <button
              key={i}
              className={`w-full p-4 ${selectedTab === tab
                ? "bg-primaryBlue text-white rounded-md"
                : !isSubnetTabValid
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-primaryBlue"
                } text-sm`}
              onClick={() => {
                if (isSubnetTabValid) {
                  setSelectedTab(tab);
                }
              }}
              disabled={!isSubnetTabValid}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-4 mt-10">
        <form onSubmit={createSubnet.handleSubmit}>
          {selectedTab === "Subnet" && (
            <div className="w-full lg:w-[50%] grid grid-cols-1 items-start gap-4">
              <div>
                <Input
                  type={"text"}
                  label="Subnet Name *"
                  name={"subnetName"}
                  onBlur={createSubnet.handleBlur}
                  onChange={createSubnet.handleChange}
                  placeholder={""}
                  value={createSubnet.values.subnetName}
                  onError={
                    createSubnet.errors.subnetName && createSubnet.touched.subnetName
                      ? createSubnet.errors.subnetName
                      : null
                  }
                />
              </div>

              <div>
                <Input
                  type={"text"}
                  label="Network Address *"
                  name={"networkAddress"}
                  onBlur={createSubnet.handleBlur}
                  onChange={createSubnet.handleChange}
                  placeholder={"Network address in CIDR format (e.g. 192.168.0.0/24, 2001:DB8::/48)"}
                  isLabelIcon={true}
                  iconTitle="Network address in CIDR format (e.g. 192.168.0.0/24, 2001:DB8::/48)"
                  value={createSubnet.values.networkAddress}
                  onError={
                    createSubnet.errors.networkAddress && createSubnet.touched.networkAddress
                      ? createSubnet.errors.networkAddress
                      : null
                  }
                />
              </div>

              <div>
                <Select
                  name="ipVersion"
                  label="IP Version"
                  options={ipVersionOptions}
                  value={createSubnet.values.ipVersion}
                  onBlur={createSubnet.handleBlur}
                  onChange={(event) =>
                    createSubnet.setFieldValue("ipVersion", event.target.value)
                  }
                  onError={
                    createSubnet.touched.ipVersion && createSubnet.errors.ipVersion
                      ? createSubnet.errors.ipVersion
                      : null
                  }
                />
              </div>
            </div>
          )}

          {selectedTab === "Subnet Details" && (
            <div className="w-full">
              <div className="w-full lg:w-[50%] grid grid-cols-1 items-start gap-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="enableDhcp"
                    name="enableDhcp"
                    checked={createSubnet.values.enableDhcp}
                    onChange={(event) =>
                      createSubnet.setFieldValue("enableDhcp", event.target.checked)
                    }
                  />
                  <label htmlFor="enableDhcp" className="text-sm text-gray-700 dark:text-gray-300">
                    Enable DHCP
                  </label>
                </div>

                <div>
                  <TextArea
                    type={"text"}
                    label="Allocation Pools"
                    name={"allocationPoolsText"}
                    onBlur={createSubnet.handleBlur}
                    onChange={createSubnet.handleChange}
                    placeholder={"Example for CIDR 192.168.0.0/24:\n192.168.0.3,192.168.0.150\n192.168.0.200,192.168.0.250\n\nEach entry: start_ip,end_ip (one per line)"}
                    isLabelIcon={true}
                    iconTitle="IP address allocation pools must be within the CIDR range. Each entry format: start_ip_address,end_ip_address (e.g., 192.168.0.3,192.168.0.150) and one entry per line."
                    value={createSubnet.values.allocationPoolsText}
                    noOfRow={6}
                    onError={
                      createSubnet.errors.allocationPoolsText && createSubnet.touched.allocationPoolsText
                        ? createSubnet.errors.allocationPoolsText
                        : null
                    }
                  />

                  {/* IP Range Information and Suggestions */}
                  {suggestedRanges && (
                    <div className="mt-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-800">
                      <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                        📊 Network Range Information
                      </h4>
                      <div className="text-xs text-blue-700 dark:text-blue-300 mb-3">
                        <p><strong>Network:</strong> {suggestedRanges.networkStart} - {suggestedRanges.networkEnd}</p>
                        <p><strong>Usable IPs:</strong> {suggestedRanges.usableStart} - {suggestedRanges.usableEnd}</p>
                      </div>

                      <h5 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                        💡 Suggested Allocation Pools:
                      </h5>
                      <div className="space-y-2">
                        {suggestedRanges.suggestions.map((suggestion, index) => (
                          <div key={index} className="flex items-center justify-between bg-white dark:bg-gray-800 p-2 rounded border">
                            <div className="flex-1">
                              <code className="text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                                {suggestion.start},{suggestion.end}
                              </code>
                              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                {suggestion.description}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                const currentValue = createSubnet.values.allocationPoolsText;
                                const newRange = `${suggestion.start},${suggestion.end}`;
                                const newValue = currentValue
                                  ? `${currentValue}\n${newRange}`
                                  : newRange;
                                createSubnet.setFieldValue("allocationPoolsText", newValue);
                              }}
                              className="ml-2 px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                            >
                              Add
                            </button>
                          </div>
                        ))}
                      </div>

                      <div className="mt-3 text-xs text-blue-600 dark:text-blue-400">
                        <p>💡 <strong>Tip:</strong> Click "Add" to automatically add a suggested range to your allocation pools.</p>
                      </div>
                    </div>
                  )}

                  {createSubnet.values.networkAddress && !suggestedRanges && (
                    <p className="text-xs text-red-500 mt-1">
                      Invalid CIDR format. Please enter a valid network address (e.g., 192.168.0.0/24)
                    </p>
                  )}

                  {createSubnet.values.networkAddress && suggestedRanges && (
                    <p className="text-xs text-gray-500 mt-1">
                      Note: Allocation pools must be within the CIDR range: {createSubnet.values.networkAddress}
                    </p>
                  )}
                </div>

                <div>
                  <TextArea
                    type={"text"}
                    label="DNS Name Servers"
                    name={"dnsServersText"}
                    onBlur={createSubnet.handleBlur}
                    onChange={createSubnet.handleChange}
                    placeholder={"IP address list of DNS name servers for this subnet. One entry per line."}
                    isLabelIcon={true}
                    iconTitle="IP address list of DNS name servers for this subnet. One entry per line."
                    value={createSubnet.values.dnsServersText}
                    noOfRow={4}
                    onError={
                      createSubnet.errors.dnsServersText && createSubnet.touched.dnsServersText
                        ? createSubnet.errors.dnsServersText
                        : null
                    }
                  />
                </div>
              </div>

              <div className="mt-6 flex gap-4">
                <button
                  type="button"
                  onClick={handlePrevious}
                  className="p-4 rounded-md border-primaryBlue text-primaryBlue text-sm"
                >
                  Previous
                </button>

                <button
                  type="submit"
                  className="p-4 rounded-md bg-primaryBlue text-white text-sm"
                >
                  Create Subnet
                </button>
              </div>
            </div>
          )}

          {selectedTab === "Subnet" && (
            <div className="mt-6">
              <button
                type="button"
                onClick={handleNext}
                className="p-4 rounded-md bg-primaryBlue text-white text-sm"
              >
                Next
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default CreateSubnet;